import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 PR-C.1 — Single source of truth for the "super_admin with no active org
 * context" case across every org-scoped page.
 *
 * Returns:
 *   - noOrgSuperAdmin: boolean. true → page must NOT fetch org-scoped data
 *     (it will 400 with "No org context") and must NOT toast. Render the
 *     shared <OrgScopeEmptyState/> instead.
 *   - onApiError(err): toast.error replacement that suppresses the 400 +
 *     "No org context" rejection (defence-in-depth — if a fetch slipped
 *     through during a state transition, still no noise).
 *
 * Usage:
 *   const { noOrgSuperAdmin, onApiError } = useOrgScope();
 *   useEffect(() => { if (!noOrgSuperAdmin) load(); }, [noOrgSuperAdmin]);
 *   ... catch (e) { onApiError(e); }
 *
 * The hook stays cheap — read-only on AuthContext, no fetch, no state.
 */
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export function isOrgScopeRejection(err) {
  // Backend returns 400 + {"detail": "No org context"} from require_org_scope.
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  return status === 400 && typeof detail === "string" && /^no org context$/i.test(detail.trim());
}

export function useOrgScope() {
  const { user, effectiveOrgId } = useAuth();
  // Super_admins can be `org_id: null` AND have no impersonation context.
  // Plain org users always have org_id set; we never want to suppress for them.
  const noOrgSuperAdmin = !!(
    user &&
    user.role === "super_admin" &&
    !user.org_id &&
    !effectiveOrgId
  );

  const onApiError = (err) => {
    if (isOrgScopeRejection(err)) return; // empty-state covers it
    toast.error(formatApiError(err));
  };

  return { noOrgSuperAdmin, onApiError };
}
